import { Rng } from "@legacy-manager/engine";

import { MEDIDAS, TONS } from "./folha";

/**
 * Qual avatar cabe num jogador daquela nacionalidade.
 *
 * O gerador mede a pele de cada avatar e guarda um degrau de 0 a 4. Aqui cada
 * nação recebe um peso para cada degrau, e o jogador sorteia dentro disso.
 *
 * **Toda nação é uma mistura, nenhuma tem um tom só.** É o que a tabela abaixo
 * garante por construção: não existe peso zerado em nenhum degrau de nenhuma
 * nação. Um elenco alemão sai mais claro em média que um colombiano, e os dois
 * saem com gente de todos os tons, que é como um elenco de futebol é.
 *
 * Os pesos são grosseiros de propósito. Eles não descrevem país nenhum: eles
 * evitam as duas coisas que ficam erradas na tela. Sortear no monte dá onze
 * jogadores sem nada a ver com a bandeira ao lado do nome, e fixar um tom por
 * país dá um elenco de clones. O meio termo é uma inclinação leve.
 *
 * **Sem nacionalidade, sorteia entre todos.** Jogador de save antigo ou de
 * nação que o mundo não gera cai na tabela neutra, que é plana.
 */

/** Peso de cada degrau de pele, do mais escuro ao mais claro. */
type Pesos = readonly [number, number, number, number, number];

const NEUTRA: Pesos = [2, 3, 3, 3, 2];

const POR_NACAO: Record<string, Pesos> = {
  BR: [3, 4, 4, 3, 2],
  CO: [3, 4, 4, 2, 2],
  UY: [1, 2, 3, 4, 3],
  AR: [1, 2, 3, 4, 3],
  PT: [2, 3, 4, 4, 2],
  ES: [1, 2, 3, 4, 3],
  IT: [1, 2, 3, 4, 3],
  FR: [3, 3, 3, 3, 3],
  EN: [2, 3, 3, 3, 4],
  DE: [2, 2, 3, 3, 4],
};

/**
 * Os avatares de cada degrau, montados uma vez.
 *
 * Refazer isto a cada ficha seria varrer os 56 a cada clique numa linha do
 * elenco, e o elenco redesenha a lista inteira quando muda a seleção.
 */
const POR_TOM: number[][] = Array.from({ length: TONS }, () => []);
MEDIDAS.forEach((medida, indice) => {
  (POR_TOM[medida.tom] ?? POR_TOM[0] ?? []).push(indice);
});

/**
 * O degrau mais próximo que tem avatar, quando o sorteado está vazio.
 *
 * A folha cresce um avatar por vez, então é normal um degrau ficar sem nenhum
 * por um tempo. Cair no vizinho é melhor que cair no monte: o tom erra por um
 * degrau em vez de errar por quatro.
 */
function degrauComGente(tom: number): number[] {
  for (let raio = 0; raio < TONS; raio += 1) {
    const abaixo = POR_TOM[tom - raio];
    if (abaixo && abaixo.length > 0) return abaixo;
    const acima = POR_TOM[tom + raio];
    if (acima && acima.length > 0) return acima;
  }
  return MEDIDAS.map((_, indice) => indice);
}

/**
 * Qual avatar este jogador usa.
 *
 * Dois sorteios separados, e é de propósito: acrescentar avatares de um tom não
 * mexe no tom de ninguém, só em qual avatar daquele tom cada um pega. Com um
 * sorteio só, cada arte nova embaralharia o elenco inteiro do mundo.
 */
export function avatarDoJogador(playerId: string, nacionalidade?: string): number {
  const pesos = (nacionalidade ? POR_NACAO[nacionalidade] : undefined) ?? NEUTRA;

  const total = pesos.reduce((soma, peso) => soma + peso, 0);
  let sorteio = new Rng(`${playerId}|tom`).int(1, total);
  let tom = 0;
  for (let degrau = 0; degrau < pesos.length; degrau += 1) {
    sorteio -= pesos[degrau] as number;
    if (sorteio <= 0) {
      tom = degrau;
      break;
    }
  }

  const candidatos = degrauComGente(tom);
  const escolhido = new Rng(`${playerId}|avatar`).int(0, candidatos.length - 1);
  return candidatos[escolhido] as number;
}
